/*
 * Supplier timing summary at the end of a search (run 24, Levan 11.10.2026): which supplier answered in how many seconds, which gave no answer
 * or an error, how many offers each brought, the slowest highlighted. Drawn by Amiran's diagnostics panel (website-web#47) in summary mode.
 * Shown only when the runtime switch `searchSupplierSummary` is true (APP_ENV_CONFIG: DEV / TEST / STG / PROD on, absent = off), the
 * agent's Kazunion Website Flag `website-supplier-answers` is on (website-flags.md R-WF20), the search is complete and the answer named its
 * suppliers. Docs: docs features/website/search-line-loader.md.
 */
import { ChangeDetectionStrategy, Component, InjectionToken, computed, inject, input } from '@angular/core';
import { SearchDiagnostics } from '@appTypes/api.types';
import { WebsiteFlags } from '@core/services/website-flags.service';
import { environment } from '@env/environment';
import { SearchDiagnosticsPanelComponent } from '../search-diagnostics/search-diagnostics-panel.component';

/** the per-environment switch; a token so a test can turn it on */
export const SEARCH_SUPPLIER_SUMMARY_ENABLED = new InjectionToken<boolean>('SEARCH_SUPPLIER_SUMMARY_ENABLED', {
  providedIn: 'root',
  factory: () => environment.searchSupplierSummary === true
});

@Component({
  selector: 'app-search-supplier-summary',
  imports: [SearchDiagnosticsPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.hidden]': '!shown()' },
  // the panel measures its own width (container query): in a centring flex parent it must stretch, not shrink to nothing
  styles: `
    :host {
      display: block;
      align-self: stretch;
      min-width: 0;
    }
    :host(.hidden) {
      display: none;
    }
  `,
  template: `
    @if (shown()) {
      <app-search-diagnostics-panel mode="summary" [diagnostics]="diagnostics()" />
    }
  `
})
export class SearchSupplierSummaryComponent {
  readonly #enabled = inject(SEARCH_SUPPLIER_SUMMARY_ENABLED);
  readonly #websiteFlags = inject(WebsiteFlags);

  readonly diagnostics = input<SearchDiagnostics | null | undefined>(null);
  /** true once the search finished (the loader is gone) */
  readonly complete = input<boolean | null | undefined>(false);

  protected readonly shown = computed(
    () =>
      this.#enabled &&
      this.#websiteFlags.supplierAnswers() &&
      !!this.complete() &&
      (this.diagnostics()?.providers?.length ?? 0) > 0
  );
}
